import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useEffect, useRef } from "react";

import { DRAFT_STALE_TIME_MS } from "../constants";
import {
  isSegmentCountable,
  toDraftRules,
  toVacancyFilterBody,
  type DraftBody,
} from "../mappers";
import {
  getNextPageParam,
  itemsInList,
  listTotal,
  needsReset,
  type VacancyPage,
  type VacancyPageParam,
} from "../vacancyPages";

import { segmentsQueryKeys } from "@/features/segments/api/queryKeys";
import type {
  SegmentVacanciesResponse,
  VacancyOverride,
} from "@/features/segments/types";
import { instance } from "@/lib/axios/instance";

const PAGE_SIZE = 25;
type UseSegmentTabVacanciesParams = Omit<
  Parameters<typeof segmentsQueryKeys.tabVacancies>[0],
  "rules"
> & {
  body: DraftBody;
  overrides: VacancyOverride[];
};

export function useSegmentTabVacancies({
  body,
  overrides,
  list,
  search,
  descending,
  filters,
}: UseSegmentTabVacanciesParams) {
  const queryClient = useQueryClient();
  const rules = toDraftRules(body);
  const queryKey = segmentsQueryKeys.tabVacancies({
    rules,
    list,
    search,
    descending,
    filters,
  });
  const latest = useRef({ overrides, queryKey });
  latest.current = { overrides, queryKey };

  const query = useInfiniteQuery<
    VacancyPage,
    AxiosError,
    InfiniteData<VacancyPage, VacancyPageParam>,
    typeof queryKey,
    VacancyPageParam
  >({
    queryKey,
    initialPageParam: { page: 1 },
    queryFn: ({ pageParam, signal }) => {
      const fetchedFor = pageParam.overrides ?? latest.current.overrides;
      return instance
        .post<SegmentVacanciesResponse>(
          "/api/v2/Segments/drafts/vacancies",
          {
            ...rules,
            vacancyOverrides: fetchedFor,
            parts: "Page",
            list,
            search,
            descending,
            ...toVacancyFilterBody(filters),
            page: pageParam.page,
            pageSize: PAGE_SIZE,
          },
          { signal },
        )
        .then((res) => ({ ...res.data, list, fetchedFor }));
    },
    getNextPageParam: (_lastPage, pages) => getNextPageParam(pages),
    enabled: isSegmentCountable(body),
    staleTime: DRAFT_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    placeholderData: (previous) =>
      previous?.pages[0]?.list === list ? previous : undefined,
  });

  const { data, isFetching, isPlaceholderData, refetch } = query;
  const pages = data?.pages ?? [];
  const items = itemsInList(pages, overrides, list);
  const shouldReset = !isPlaceholderData && needsReset(pages, overrides);
  const resetAttempt = shouldReset
    ? JSON.stringify([queryKey, overrides])
    : null;
  const lastResetAttempt = useRef<string | null>(null);

  useEffect(() => {
    if (
      !resetAttempt ||
      isFetching ||
      lastResetAttempt.current === resetAttempt
    )
      return;
    lastResetAttempt.current = resetAttempt;
    queryClient.setQueryData<InfiniteData<VacancyPage, VacancyPageParam>>(
      latest.current.queryKey,
      (current) =>
        current && {
          pages: current.pages.slice(0, 1),
          pageParams: current.pageParams.slice(0, 1),
        },
    );
    void refetch();
  }, [resetAttempt, isFetching, queryClient, refetch]);

  return { query, items, totalCount: listTotal(pages, overrides, list) };
}
